// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
// import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors"; // Store Custom Code
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import Button from "@insite/mobius/Button"; // Starter Custom Code
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import styled, { css, useTheme } from "styled-components";

/* Starter Custom Code - Start */
// import Button, { ButtonSizeVariants } from "@insite/mobius/Button"; // Foundation
import getButtonSizeVariantField from "@store/Framework/Common/Fields/Toolkit/buttonSizeVariant";
import getButtonVariantField from "@store/Framework/Common/Fields/Toolkit/buttonVariant";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
/* Starter Custom Code - End */

// Foundation --->
// import Button, { ButtonPresentationProps, ButtonSizeVariants } from "@insite/mobius/Button";
import { ButtonIcon } from "@insite/mobius/Button";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps, ButtonProps, ButtonSizeVariants } from "@store/MobiusOverrides/Button/Props";
// <--- Foundation

/* Store Custom Code */
import { useGetLink } from "@store/Framework/Store/Links/LinksSelectors";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    alignment = "alignment",
    customCSS = "customCSS",
    // Starter Custom Code
    sizeVariant = "sizeVariant",
    // Foundation
    hasFixedWidth = "hasFixedWidth",
    buttonFixedWidth = "buttonFixedWidth",
    openInNewWindow = "openInNewWindow",
    rightIcon = "rightIcon",
    leftIcon = "leftIcon",
}

/* Starter Custom Code - Start */
const variantField = getButtonVariantField(fields.variant);
const sizeVariantField = getButtonSizeVariantField(fields.sizeVariant);
/* Starter Custom Code - End */

/* Store Custom Code - added export */
export interface OwnProps extends WidgetProps {
    /* Store Custom Code */
    buttonProps?: ButtonProps & { "data-test-selector"?: string };
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary" | "primaryDark";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        // Starter Custom Code
        [fields.sizeVariant]: ButtonSizeVariants;
        // Foundation
        [fields.hasFixedWidth]: boolean;
        [fields.buttonFixedWidth]?: number;
        [fields.openInNewWindow]?: boolean;
        // Store Custom Code - >
        [fields.leftIcon]: string;
        [fields.rightIcon]: string;
        // Store Custom Code - <---
    };
}

export interface ButtonStyles {
    wrapperStyles: InjectableCss;
}

/* Starter Custom Code - Start */
const wrapperStyles: InjectableCss = widgetWrapperStyles;
/* Starter Custom Code - End */

export const CmsButton: FC<OwnProps> = ({ fields, buttonProps }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!link.url) {
            return;
        }

        // Foundation --->
        if (fields.openInNewWindow) {
            window.open(link.url);
        } else if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
        // <--- Foundation
    };

    const styles = useMergeStyles("button", { wrapperStyles });

    // Store Custom Code - >
    const buttonStyles: ButtonPresentationProps | undefined = {
        css: css`
            display: flex;
            align-items: center;

            justify-content: center;
            ${ButtonIcon} {
                font-weight: ${({ theme }) => theme.design.typography.weight.bold};

                position: static;
            }

            ${fields.hasFixedWidth &&
            css`
                width: ${fields.buttonFixedWidth}px;
            `};
        `,
    };
    // <---

    const customCssWrapper = {
        css: css`
            ${styles?.wrapperStyles?.css /* Starter Custom Code */}
            height: fit-content;
            ${fields.customCSS}
        `,
    };
    const leftIcon = fields?.leftIcon || undefined;
    const rightIcon = fields?.rightIcon || undefined;
    const both = !!(leftIcon && rightIcon);
    const position = rightIcon && !leftIcon ? "right" : "left";
    return (
        /* Starter Custom - Merged StyledWrapper and ContentWrapper styles */
        <ContentWrapper alignment={fields.alignment} {...customCssWrapper} className="wrapper">
            <Button
                variant={variantField.parse(fields.variant) /* Starter Custom Code */}
                icon={
                    leftIcon || rightIcon
                        ? {
                              both,
                              position,
                              src: (leftIcon ?? rightIcon)!,
                              rightIcon: rightIcon || undefined,
                          }
                        : undefined
                }
                sizeVariant={fields.sizeVariant} /* Starter Custom Code */
                onClick={onClick}
                // Store Custom Code - new variant is working with mergecss on while old one isn't.
                // Best guess is that some theme function is applying extra styles that isn't being applied to the new one.
                mergeCss={fields.variant === "primaryDark"}
                className="button"
                {
                    ...buttonStyles /* Foundation */
                }
                {
                    ...buttonProps /* Store Custom Code */
                }
            >
                {fields.label}
            </Button>
        </ContentWrapper>
    );
};

/* Starter Custom Code - Start */
const defaultCustomCss = `& {
}

.button {
}
`;
/* Starter Custom Code - End */

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: CmsButton,
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                ...variantField.definition, // Starter Custom Code
                // Starter Removed - name
                // Starter Removed - displayName
                // Starter Removed - editorTemplate
                // Starter Removed - options
                // Starter Removed - defaultValue
                fieldType: "Contextual",
                tab: basicTab,
            },
            /* Starter Custom Code - Start */
            {
                ...sizeVariantField.definition,
                tab: basicTab,
            },
            /* Starter Custom Code - End */
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                // Store Custom Code - Translatable for external links.
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },

            // Foundation --->
            {
                fieldType: "General",
                name: fields.hasFixedWidth,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.buttonFixedWidth,
                displayName: "Button Fixed Width",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "Translatable",
                isVisible: widget => widget.fields.hasFixedWidth,
            },
            {
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.leftIcon,
                displayName: "Left Icon",
                editorTemplate: "IconPickerField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.rightIcon,
                displayName: "Right Icon",
                editorTemplate: "IconPickerField",
                fieldType: "General",
                defaultValue: "",
            },

            // <-- Foundation
        ],
    },
};

export default widgetModule;
